/**
 * Mock data for the Breakdown Worksheet. Nothing here talks to the API — this
 * page exists to show a clinic a proposed experience, not to run one.
 *
 * Shape, and why:
 *
 *   family
 *     ├── codes[]        MANY codes per family. A family is the unit a carrier
 *     │                  quotes coverage for; the codes under it are what
 *     │                  actually gets billed, and they do not always agree.
 *     ├── category       our default, editable — the same procedure sits in
 *     │                  different categories from carrier to carrier
 *     └── exception      exceptions belong to the family they qualify, not to a
 *                        single free-text blob at the bottom of the page
 *
 * The codes under a family are LABELS — the billing codes that family covers,
 * shown comma-separated so staff recognise the family by what they bill. The
 * coverage, frequency and exceptions are stated once, on the family.
 *
 * `pool` names the dollar maximum a family draws from. Carriers state one
 * maximum across many families — Manulife prints it as a single cell merged
 * down eleven rows — so the pool is modelled as a shared key rather than a
 * number repeated on every row. Repetition is what makes a maximum look like
 * eleven different maximums.
 */

import {
  COVERAGE_CATEGORIES, PROCEDURE_FAMILIES, PROCEDURE_LINES, familyByKey,
} from "../TreatmentPlans/lib/procedureFamilies";

// The four coverage categories are the backend's COVERAGE_TYPES — the same
// names the category cards and every stored row use.
export const CATEGORIES = COVERAGE_CATEGORIES;

/**
 * The dollar maximums, each stated ONCE. Families point at a pool by key.
 */
export const POOLS = {
  annual: { key: "annual", label: "Basic + Major", amount: 1500, period: "per calendar year", resets: "Jun 1" },
  ortho:  { key: "ortho",  label: "Orthodontics",  amount: 2500, period: "per lifetime", note: "dependants under 18" },
};

export const CATEGORY_COLOR = {
  Preventative: "green",
  Basic: "geekblue",
  Major: "gold",
  Orthodontics: "purple",
};

/**
 * The families, each carrying the codes a practice actually asks about — the
 * shared table in TreatmentPlans/lib/procedureFamilies.js, which the text and
 * print exports read too, so the worksheet and the exports can never disagree
 * about what "Denture" or "Perio scaling" means.
 *
 * `askBasicMajor` marks the three the paper form prints "Basic / Major" beside.
 */
export const FAMILIES = PROCEDURE_FAMILIES;

/**
 * Frequency is quoted at a FINER grain than coverage.
 *
 * Manulife's own breakdown proves it: Polishing, Prophylaxis and Preventative
 * Scaling are one coverage family but three separate frequency rows, each with
 * its own adult and child limit. So frequency gets its own list rather than
 * being flattened onto the family — which is what "limited by age and procedure
 * code" means in practice. These are the shared table's lines flagged as
 * frequency rows, keyed by the same line keys the exports use.
 */
export const FREQUENCY_GROUPS = PROCEDURE_LINES
  .filter(function (line) { return line.frequencyRow; })
  .map(function (line) {
    return { key: line.key, name: line.label, family: familyByKey[line.family].name, codes: [line.templateCode] };
  });

/**
 * A filled worksheet so the display view has something to show.
 *
 * `perCode` only carries entries where a code DIFFERS from its family — a blank
 * per-code entry means "same as the family", which is the common case and should
 * not clutter the table.
 */
export const SAMPLE_STATE = {
  plan: {
    employer: "Bell Canada",
    carrier: "Sun Life",
    carrierPhone: "1-800-361-6212",
    policyNumber: "050872",
    certificateNumber: "0000027274",
    accountNumber: "40378",
    edi: true,
    assignment: "Y",
  },
  money: {
    feeGuide: "-1",
    specialistFee: "Y",
    maxBasic: "1500",
    maxMajor: "1500",
    maxCombined: true,
    deductibleSingle: "25",
    deductibleFamily: "50",
    renewsMonth: "06",
    renewsDay: "01",
    renewsYear: "2026",
  },
  eligibility: {
    dependentAge: "19",
    ftStudentAge: "25",
    examLimitations: "Any 2 exams in a year",
    nitrous: "N",
    limitations: "",
  },
  hygiene: {
    recallCycle: "9",
    recallCycleChildren: "6",
    recallSplitAge: "18",
    scalingUnits: "4",
    scalingBasis: "Year",
    prevScalingPct: "90",
    perioScalingDiffers: true,
    perioScalingPct: "50",
    perioSurgeryPct: "50",
    oeMonths: "24",
    panMonths: "36",
    bwMonths: "12",
    sealants: "Y",
    fluoride: "Y",
    fluorideAgeLimit: "14",
    ohi: "Y",
    compositesMolars: "Y",
    relinePct: "100",
  },
  families: {
    exams: {
      category: "Preventative", pct: "100",
      exception: "Recall 9M over 18Y, 6M under 18Y. Complete exam once every 3 calendar years.",
    },
    xrays: {
      category: "Preventative", pct: "100",
      exception: "",
    },
    hygiene: {
      category: "Preventative", pct: "90",
      exception: "Perio scaling (11113) paid at 50%, and only with a periodontist.",
    },
    fluoride: {
      category: "Preventative", pct: "100",
      exception: "Covered to age 14 only.",
    },
    sealants: {
      category: "Preventative", pct: "100",
      exception: "",
    },
    ohi: {
      category: "Preventative", pct: "100",
      exception: "",
    },
    appliances: { category: "Preventative", pct: "100", exception: "Night guard once every 5 years." },
    restorative: {
      category: "Basic", pct: "80",
      exception: "Composites on molars (23322) are covered.",
    },
    endo:      { category: "Basic", pct: "80", exception: "" },
    periosurg: { category: "Basic", pct: "50", exception: "" },
    oralsurg:  { category: "Basic", pct: "80", exception: "" },
    crown:     { category: "Major", pct: "50", exception: "" },
    bridge:    { category: "Major", pct: "50", exception: "" },
    denture:   { category: "Major", pct: "50",
                 exception: "Reline (56241) covered in full." },
    ortho:     { category: "Orthodontics", pct: "50",
                 orthoAnnual: "", orthoLTM: "2500", orthoAgeLimit: "18",
                 exception: "Dependent children only, under 22 years old." },
    adjunctive: { category: "Basic", pct: "80", exception: "" },
  },
  // Verbatim from the Manulife breakdown, including its own phrasing.
  frequency: {
    bitewings:           { adult: "2 time(s) every 1 year(s)",  child: "2 time(s) every 1 year(s)" },
    pan:                 { adult: "every 36 months or once every 3 years", child: "every 36 months or once every 3 years" },
    fluoride:            { adult: "2 time(s) every 1 year(s)",  child: "2 time(s) every 1 year(s)" },
    exam:                { adult: "Once every 3 calendar years", child: "Once every 3 calendar years" },
    polishing:           { adult: "2 unit(s) every 1 year(s)",  child: "2 unit(s) every 1 year(s)" },
    preventativeScaling: { adult: "2 units per accumulation year", child: "2 units per accumulation year" },
    recall:              { adult: "2 time(s) every 1 year(s)",  child: "2 time(s) every 1 year(s)" },
  },
  verifiedBy: "Irma",
  verifiedOn: "Sep 02 / 26",
};

/**
 * Faked patient-side data for "Available today".
 *
 * Recare only. Perio units used and maximum drawn down are deliberately absent:
 * both need paid-claim history we do not hold, and a number we cannot compute is
 * worse on this page than no number at all.
 */
export const SAMPLE_PATIENT = {
  name: "Robert Touesnard",
  recare: [
    { label: "Recall exam", status: "due", detail: "eligible today" },
    { label: "Oral exam", status: "not-due", detail: "next Mar 4, 2027" },
    { label: "Polish", status: "due", detail: "eligible today" },
    { label: "Bitewings", status: "not-due", detail: "next Nov 12, 2026" },
    { label: "Fluoride", status: "due", detail: "eligible today" },
  ],
  nextAppointment: "Sep 14, 2026",
};
